import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '@/services/api/apiService';

/* -------------------- MODELS -------------------- */

export interface CustomerReview {
  id: number;
  imageUrl?: string;
  isActive: boolean;
  customerName: string;
  cookProfileId: number;
  customerRating: number;
  foodScheduleId: number;
  customerReviewComment?: string;
}

export interface FoodReview {
  reviews: CustomerReview[];
  foodName: string;
  foodImageUrl: string;
  mealTypeName: string;
  scheduledDate: string;
  foodMenuItemId: number;
  averageRatingPerFood: number;
}

interface CustomSearchCustomerFoodReviewsState {
  data: FoodReview[];
  loading: boolean;
  error: string | null;
}

/* -------------------- API RESPONSE -------------------- */

export interface FoodReviewApiResponse {
  data: {
    data: FoodReview[];
    db_status: string;
  };
  isSuccess: boolean;
  status: number;
}

/* -------------------- ENV -------------------- */

const PORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const URL = import.meta.env.VITE_API_FOOD_ORDER_URL;

/* -------------------- THUNK -------------------- */

export const customSearchCustomerFoodReviews = createAsyncThunk<
  FoodReview[],
  { foodMenuItemId: number },
  { rejectValue: string }
>(
  'customSearchCustomerFoodReviews/customSearchCustomerFoodReviews',
  async (params, { rejectWithValue }) => {
    try {
      // response is typed correctly
      const response = await apiService.get<FoodReviewApiResponse>(
        'custom_search_customer_food_review',
        PORT,
        URL,
        { params },
      );

      // return the actual FoodReview array
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred');
    }
  },
);

/* -------------------- SLICE -------------------- */

const initialState: CustomSearchCustomerFoodReviewsState = {
  data: [],
  loading: false,
  error: null,
};

const customSearchCustomerFoodReviewsSlice = createSlice({
  name: 'customSearchCustomerFoodReviews',
  initialState,
  reducers: {
    clear: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(customSearchCustomerFoodReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        customSearchCustomerFoodReviews.fulfilled,
        (state, action: PayloadAction<FoodReview[]>) => {
          state.loading = false;
          state.data = action.payload;
        },
      )
      .addCase(customSearchCustomerFoodReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch food reviews';
      });
  },
});

export const { clear } = customSearchCustomerFoodReviewsSlice.actions;
export default customSearchCustomerFoodReviewsSlice.reducer;
